import dayjs from 'dayjs';
import { endOfWeek, startOfWeek } from '@/util/utils';

function getAllTimeRange(today = new Date()) {
  return { start: dayjs('1970-01-02'), end: dayjs(today).endOf('d') };
}

function getAllTimeRangeInFuture(today = new Date()) {
  return { start: dayjs('1970-01-02'), end: dayjs(today).add(10, 'year') };
}

function getTodayRange(today = new Date()) {
  return { start: dayjs(today), end: dayjs(today) };
}

function getThisWeekRange(today = new Date(), weekStartsOn = 1) {
  return {
    start: startOfWeek(today, weekStartsOn),
    end: endOfWeek(today, weekStartsOn),
  };
}

function getThisMonthRange(today = new Date()) {
  return { start: dayjs(today).startOf('M'), end: dayjs(today).endOf('M') };
}

function getThisYearRange(today = new Date()) {
  return { start: dayjs(today).startOf('y'), end: dayjs(today).endOf('y') };
}

function getLastWeekRange(today = new Date(), weekStartsOn = 1) {
  return {
    start: dayjs(startOfWeek(today, weekStartsOn)).subtract(1, 'w'),
    end: dayjs(endOfWeek(today, weekStartsOn)).subtract(1, 'w'),
  };
}

function getLastMonthRange(today = new Date()) {
  const firstDayOfThisMonth = dayjs(today).startOf('M');
  const lastDayOfPreviousMonth = dayjs(firstDayOfThisMonth).subtract(1, 'd');
  const firstDayOfPreviousMonth = dayjs(lastDayOfPreviousMonth).startOf('M');
  return { start: firstDayOfPreviousMonth, end: lastDayOfPreviousMonth };
}

function getLastYearRange(today = new Date()) {
  const firstDayOfThisYear = dayjs(today).startOf('y');
  const lastDayOfPreviousYear = dayjs(firstDayOfThisYear)
    .subtract(1, 'd')
    .endOf('d');
  const firstDayOfPreviousYear = dayjs(lastDayOfPreviousYear).startOf('y');
  return { start: firstDayOfPreviousYear, end: lastDayOfPreviousYear };
}

/**
 * Resolve a predefined range name (as shown in VDatePicker) to a concrete range.
 * Returns null for unknown names.
 * @param {string} name 'All Time' | 'Today' | 'This Week' | 'This Month' | 'This Year' |
 *   'Last Week' | 'Last Month' | 'Last Year'
 * @param {{ today?: Date, weekStartsOn?: number, isAllTimeInFuture?: boolean }} options
 * @returns {{ start: dayjs.Dayjs, end: dayjs.Dayjs } | null}
 */
function getPredefinedRange(
  name,
  { today = new Date(), weekStartsOn = 1, isAllTimeInFuture = false } = {},
) {
  const ranges = {
    'All Time': () => (isAllTimeInFuture ? getAllTimeRangeInFuture(today) : getAllTimeRange(today)),
    Today: () => getTodayRange(today),
    'This Week': () => getThisWeekRange(today, weekStartsOn),
    'This Month': () => getThisMonthRange(today),
    'This Year': () => getThisYearRange(today),
    'Last Week': () => getLastWeekRange(today, weekStartsOn),
    'Last Month': () => getLastMonthRange(today),
    'Last Year': () => getLastYearRange(today),
  };

  if (!Object.prototype.hasOwnProperty.call(ranges, name)) return null;

  return ranges[name]();
}

export {
  getPredefinedRange,
  getAllTimeRange,
  getTodayRange,
  getThisWeekRange,
  getThisMonthRange,
  getThisYearRange,
  getLastWeekRange,
  getLastMonthRange,
  getLastYearRange,
  getAllTimeRangeInFuture,
};
